import { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp } from 'lucide-react';
import TeamLogo from './TeamLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Sticky ladder panel.
 * Desktop: full table.
 * Mobile: collapsible toggle.
 * Highlights rows that match any team in `highlightTeams` (Set of team names).
 * Clicking a row calls onTeamClick(team) — used to scroll to the matching fixture.
 */
export default function Ladder({ sport, highlightTeams = new Set(), onTeamClick, teamLogos = {} }) {
  const [ladder, setLadder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true); // mobile default

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/ladder/${sport}`, { withCredentials: true });
        if (!cancelled) setLadder(res.data?.ladder || []);
      } catch (_) {
        if (!cancelled) setLadder([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sport]);

  const accent = sport === 'AFL' ? '#E00000' : sport === 'NRL' ? '#007A33' : sport === 'EPL' ? '#37003C' : sport === 'AL' ? '#F0423E' : '#4338CA';
  const isWC = sport === 'WC';

  const renderRow = (row) => {
    const isHl = highlightTeams.has(row.team);
    return (
      <button
        key={row.team + (row.group || '')}
        onClick={() => onTeamClick?.(row.team)}
        className={`w-full grid grid-cols-[28px_1fr_28px_28px_28px_38px] gap-1.5 items-center px-2 py-1.5 text-xs text-left transition-colors ${
          isHl
            ? 'bg-amber-50 ring-1 ring-amber-300'
            : 'hover:bg-gray-50'
        }`}
        data-testid={`ladder-row-${row.team.replace(/\s/g, '-')}`}
      >
        <span className="text-gray-500 font-mono">{row.position}</span>
        <span className="flex items-center gap-1.5 min-w-0">
          <TeamLogo teamName={row.team} logoUrl={teamLogos[row.team]} size={18} />
          <span className="truncate font-medium text-[#1A1A1A]">{row.team}</span>
          {isWC && row.world_ranking != null && (
            <span
              className="text-[9px] font-bold text-gray-400 tabular-nums flex-shrink-0"
              title={`FIFA World Ranking #${row.world_ranking}`}
            >
              #{row.world_ranking}
            </span>
          )}
        </span>
        <span className="text-center text-gray-500 tabular-nums">{row.played}</span>
        <span className="text-center text-gray-600 tabular-nums">{row.wins}</span>
        <span className="text-center text-gray-600 tabular-nums">{row.losses}</span>
        <span className="text-right font-bold tabular-nums" style={{ color: accent }}>{row.points}</span>
      </button>
    );
  };

  // Group WC entries by group letter
  const grouped = (() => {
    if (!isWC) return null;
    const m = new Map();
    ladder.forEach(r => {
      const k = r.group || '?';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    });
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  })();

  return (
    <aside className="tipster-card !p-0 overflow-hidden lg:sticky lg:top-20" data-testid={`ladder-${sport.toLowerCase()}`}>
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-3 py-2.5 border-b border-gray-200 lg:cursor-default lg:hover:bg-transparent hover:bg-gray-50"
      >
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          <h3 className="text-xs uppercase tracking-wider font-bold text-[#1A1A1A]">
            {isWC ? '2026 World Cup Groups' : sport === 'AL' ? 'A-League 2026/27 Ladder' : `${sport} 2026 Ladder`}
          </h3>
        </div>
        <span className="lg:hidden">
          {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
        </span>
      </button>

      <div className={`${collapsed ? 'hidden' : 'block'} lg:block`}>
        <div className="grid grid-cols-[28px_1fr_28px_28px_28px_38px] gap-1.5 items-center px-2 py-1.5 text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
          <span>#</span>
          <span>Team</span>
          <span className="text-center">P</span>
          <span className="text-center">W</span>
          <span className="text-center">L</span>
          <span className="text-right">Pts</span>
        </div>
        {loading && (
          <div className="px-3 py-4 text-xs text-gray-400 text-center">Loading ladder…</div>
        )}
        {!loading && ladder.length === 0 && (
          <div className="px-3 py-4 text-xs text-gray-400 text-center">Ladder unavailable</div>
        )}
        {!loading && isWC && grouped && grouped.map(([letter, rows]) => (
          <div key={letter}>
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold text-[#4338CA] bg-indigo-50/40 border-b border-gray-100">
              Group {letter}
            </div>
            {rows.map(renderRow)}
          </div>
        ))}
        {!loading && !isWC && ladder.map(renderRow)}
      </div>
    </aside>
  );
}
